import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { RawIntlProvider } from 'react-intl';
import type { Music24Locale } from 'fym-shared/src/music24/music24Locales';
import { TypedMemo } from '../../common/types';
import { getSystemLocale } from '../../../utils/deviceInfo/getUserLocale';
import { isNativeEmbedPage } from '../isNativeEmbed';
import { createIntl } from '../views/createIntl';
import { AppLocaleSource, readStoredAppLocale, storeAppLocale } from './appLocaleStorage';

export interface AppLocaleContextValue {
  /** The language the user picked; null is "Automatic" (the browser language). */
  preference: Music24Locale | null;
  /** The locale the UI uses now. */
  locale: string;
  /**
   * Switches the UI at once and keeps the choice in the browser. It does not save it to the user.
   * `source` says who picked it: the account (default) or a signed-out visitor.
   */
  setPreference: (preference: Music24Locale | null, source?: AppLocaleSource) => void;
}

const AppLocaleContext = createContext<AppLocaleContextValue>({
  preference: null,
  locale: 'en',
  setPreference: () => undefined,
});

/** The language of the UI and the setter for it. */
export const useAppLocale = (): AppLocaleContextValue => useContext(AppLocaleContext);

/**
 * The intl of the whole app. The locale is the language the user picked, else the browser
 * language. A new pick builds a new intl, and every `useIntl` reader draws again: no reload.
 *
 * The native web view keeps the device language, as the native app around it does.
 */
const AppIntlProviderComponent: React.FC<React.PropsWithChildren> = ({ children }) => {
  const followsDevice = useMemo(() => isNativeEmbedPage(), []);
  const [preference, setPreferenceState] = useState<Music24Locale | null>(() =>
    followsDevice ? null : readStoredAppLocale()
  );

  const locale = preference ?? getSystemLocale();
  const intl = useMemo(() => createIntl({ locale }), [locale]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const setPreference = useCallback(
    (next: Music24Locale | null, source: AppLocaleSource = 'account') => {
      if (followsDevice) {
        return;
      }
      storeAppLocale(next, source);
      setPreferenceState(next);
    },
    [followsDevice]
  );

  const value = useMemo(() => ({ preference, locale, setPreference }), [locale, preference, setPreference]);

  return (
    <AppLocaleContext.Provider value={value}>
      <RawIntlProvider value={intl}>{children}</RawIntlProvider>
    </AppLocaleContext.Provider>
  );
};

export const AppIntlProvider = TypedMemo(AppIntlProviderComponent);
